import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import { SortingState } from "@tanstack/react-table";
import { usePost } from "../API";
import { logAdminPanelError } from "../utils/adminPanel.utils";
import { AuditLog, FilterParams, FilterOptions } from "../AdminPanel/AuditLogs/types";


type AuditLogsContextType = {
  auditLogs: Array<AuditLog>;
  getAuditLogs: (params?: { page?: number; limit?: number; sorting?: SortingState; search?: string; filters?: FilterParams }) => void;
  isAuditLogsLoading: boolean;
  isAuditLogsSearching: boolean;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  auditLogsSearch: string;
  auditLogsSearchDisplay: string;
  auditLogsSorting: SortingState;
  auditLogsFilters: FilterParams;
  filterOptions: FilterOptions | null;
  onAuditLogsPaginationChange: (pagination: { pageIndex: number; pageSize: number }) => void;
  onAuditLogsSearchChange: (search: string) => void;
  onAuditLogsSortingChange: React.Dispatch<React.SetStateAction<SortingState>>;
  onAuditLogsFiltersChange: (filters: FilterParams) => void;
};

type AuditLogsProviderProps = {
  children: React.ReactNode;
};

const AuditLogsContext = createContext<AuditLogsContextType | undefined>(undefined);

export const useAuditLogs = (): AuditLogsContextType => {
  const context = useContext(AuditLogsContext);
  if (!context) {
    throw new Error("useAuditLogs must be used within an AuditLogsProvider");
  }
  return context;
};

export const AuditLogsProvider: React.FC<AuditLogsProviderProps> = ({ children }) => {
  const [auditLogs, setAuditLogs] = useState<Array<AuditLog>>([]);
  const [isAuditLogsLoading, setIsAuditLogsLoading] = useState(false);
  const [isAuditLogsSearching, setIsAuditLogsSearching] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [auditLogsSearch, setAuditLogsSearch] = useState('');
  const [auditLogsSearchDisplay, setAuditLogsSearchDisplay] = useState('');
  const [auditLogsSorting, setAuditLogsSorting] = useState<SortingState>([]);
  const [auditLogsFilters, setAuditLogsFilters] = useState<FilterParams>({});
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  const getAuditLogsData = usePost("/api/admin_panel/user_audit_log");

  const getAuditLogs = async (params?: { page?: number; limit?: number; sorting?: SortingState; search?: string; filters?: FilterParams }) => {
    setIsAuditLogsLoading(true);
    try {
      // Convert SortingState to the backend format
      const sortingParam = params?.sorting !== undefined ? params.sorting : auditLogsSorting;
      const backendSorting = sortingParam.map(sort => ({
        sortBy: sort.id as 'event' | 'timestamp' | 'userName',
        sortOrder: sort.desc ? 'desc' as const : 'asc' as const
      }));

      const searchParam = params?.search !== undefined ? params.search : auditLogsSearch;
      const filtersParam = params?.filters !== undefined ? params.filters : auditLogsFilters;
      
      const requestParams = {
        ...(params?.page && { page: params.page }),
        ...(params?.limit && { limit: params.limit }),
        ...(backendSorting.length > 0 && { sorting: backendSorting }),
        ...(searchParam && { search: searchParam }),
        ...(Object.keys(filtersParam).length > 0 && { filters: filtersParam })
      };

      // Try the API call and catch any errors
      const result = await getAuditLogsData(requestParams).catch((err) => {
        console.error("getAuditLogsData threw error:", err);
        return undefined;
      });

      if (result && result.success && result.data) {
        setAuditLogs(result.data.auditLogs?.data || []);
        setTotal(result.data.auditLogs?.total || 0);
        setPage(result.data.auditLogs?.page || 1);
        setLimit(result.data.auditLogs?.limit || 10);
        setTotalPages(result.data.auditLogs?.totalPages || 0);
        setFilterOptions(result.data.auditLogs?.filterOptions || null);
      } else if (result && result.error) {
        logAdminPanelError(result.error, result.message);
      } else {
        console.error("Audit logs API returned undefined or invalid response:", result);
        console.log("Setting empty array as fallback");
        setAuditLogs([]);
      }
    } catch (error) {
      console.error("Error calling audit logs API:", error);
      setAuditLogs([]);
    } finally {
      setIsAuditLogsLoading(false);
    }
  };

  const onAuditLogsPaginationChange = useCallback(
    (pagination: { pageIndex: number; pageSize: number }) => {
      // Convert 0-based pageIndex to 1-based page for API
      const page = pagination.pageIndex + 1;
      const limit = pagination.pageSize;

      getAuditLogs({ page, limit, sorting: auditLogsSorting, search: auditLogsSearch, filters: auditLogsFilters });
    },
    [getAuditLogs, auditLogsSorting, auditLogsSearch, auditLogsFilters]
  );

  const onAuditLogsSortingChange = useCallback(
    (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(auditLogsSorting) : updaterOrValue;
      setAuditLogsSorting(newSorting);
      getAuditLogs({ page: 1, limit, sorting: newSorting, search: auditLogsSearch, filters: auditLogsFilters });
    },
    [getAuditLogs, limit, auditLogsSorting, auditLogsSearch, auditLogsFilters]
  );

  const onAuditLogsSearchChange = useCallback((search: string) => {
    // Update display value immediately for UI responsiveness
    setAuditLogsSearchDisplay(search);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set searching state if we have a search value
    if (search.trim() !== auditLogsSearch.trim()) {
      setIsAuditLogsSearching(true);
    }
    
    // Debounce the actual search
    searchTimeoutRef.current = setTimeout(async () => {
      setAuditLogsSearch(search);
      await getAuditLogs({ page: 1, limit, sorting: auditLogsSorting, search, filters: auditLogsFilters });
      setIsAuditLogsSearching(false);
    }, 500);
  }, [getAuditLogs, auditLogsSorting, limit, auditLogsSearch, auditLogsFilters]);

  const onAuditLogsFiltersChange = useCallback((filters: FilterParams) => {
    setAuditLogsFilters(filters);
    getAuditLogs({ page: 1, limit, sorting: auditLogsSorting, search: auditLogsSearch, filters });
  }, [getAuditLogs, limit, auditLogsSorting, auditLogsSearch]);

  const contextValue: AuditLogsContextType = {
    auditLogs,
    getAuditLogs,
    isAuditLogsLoading,
    isAuditLogsSearching,
    total,
    page,
    limit,
    totalPages,
    auditLogsSearch,
    auditLogsSearchDisplay,
    auditLogsSorting,
    auditLogsFilters,
    filterOptions,
    onAuditLogsPaginationChange,
    onAuditLogsSearchChange,
    onAuditLogsSortingChange,
    onAuditLogsFiltersChange,
  };

  return <AuditLogsContext.Provider value={contextValue}>{children}</AuditLogsContext.Provider>;
};
